import Splide from '@splidejs/splide';
import enquire from 'enquire.js';
import { Responsive, Translations } from '../utils';
import './avvisi-gara.scss';

const COMPONENT_NAME = 'avvisi-gara';

const BEM = {
  BLOCK: COMPONENT_NAME,
  TAB: `${COMPONENT_NAME}__tab`,
  PANEL: `${COMPONENT_NAME}__panel`,
  ACCORDION_TOGGLE: `${COMPONENT_NAME}__accordion-toggle`,
};

const JS_SELECTORS = {
  NAV_SLIDER: '.js-gcc-nav-slider',
  ARROW_PREV: '.js-gcc-arrow-prev',
  ARROW_NEXT: '.js-gcc-arrow-next',
};

const CLASSES = {
  IS_ACTIVE: 'is-active',
};

/**
 * version    1.0
 * class      AvvisiGara
 * classdesc  Tab component con accordion interno.
 *            Desktop: frecce custom prev/next + flex layout.
 *            Mobile: Splide slider con pagination dots.
 */
export class AvvisiGara {
  constructor() {
    this._splide = null;
    this._findUIElements();
    if (!this._els.block) return;
    this._bindTabEvents();
    this._bindArrowEvents();
    this._bindAccordionEvents();
    this._updateArrows();
    this._initResponsive();
    this._fixEuroLineBreak();
  }

  _findUIElements() {
    this._els = {
      block: document.querySelector(`.${BEM.BLOCK}`),
    };

    if (!this._els.block) return;

    this._els.tabs = Array.from(this._els.block.querySelectorAll(`.${BEM.TAB}`));
    this._els.panels = Array.from(this._els.block.querySelectorAll(`.${BEM.PANEL}`));
    this._els.accordionToggles = Array.from(
      this._els.block.querySelectorAll(`.${BEM.ACCORDION_TOGGLE}`),
    );
    this._els.navSlider = this._els.block.querySelector(JS_SELECTORS.NAV_SLIDER);
    this._els.arrowPrev = this._els.block.querySelector(JS_SELECTORS.ARROW_PREV);
    this._els.arrowNext = this._els.block.querySelector(JS_SELECTORS.ARROW_NEXT);
  }

  // Responsive: Splide sotto i 1280px (max-width: 1279px)
  _initResponsive() {
    if (!this._els.navSlider) return;

    enquire.register(Responsive.getBreakpointMaxMediaQuery('lg'), {
      match: () => this._initSlider(),
      unmatch: () => this._destroySlider(),
    });
  }

  _initSlider() {
    if (this._splide) return;

    this._splide = new Splide(this._els.navSlider, {
      type: 'slide',
      arrows: false,
      pagination: true,
      autoWidth: true,
      gap: 0,
      drag: 'free',
      i18n: Translations.getSplideI18n(),
    });

    // Dot/swipe → attiva la tab corrispondente
    this._splide.on('moved', (newIndex) => {
      this._activateTab(this._els.tabs[newIndex]);
    });

    this._splide.mount();

    // Porta lo slider alla tab già attiva all'init
    const activeIndex = this._els.tabs.findIndex((tab) => tab.classList.contains(CLASSES.IS_ACTIVE));
    if (activeIndex > 0) this._splide.go(activeIndex);
  }

  _destroySlider() {
    if (!this._splide) return;
    this._splide.destroy(true);
    this._splide = null;

    if (this._els.navSlider) {
      // Rimuove la pagination dal DOM (destroy(true) non la rimuove sempre)
      const pagination = this._els.navSlider.querySelector('.splide__pagination');
      if (pagination) pagination.remove();
      // Resetta inline styles residui (transform translateX, transition, ecc.)
      const track = this._els.navSlider.querySelector('.splide__track');
      const list = this._els.navSlider.querySelector('.splide__list');
      if (track) track.removeAttribute('style');
      if (list) list.removeAttribute('style');
    }
  }

  // Tab switching
  _bindTabEvents() {
    this._els.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this._activateTab(tab));
      tab.addEventListener('keydown', (e) => this._onTabKeydown(e));
    });
  }

  _activateTab(targetTab) {
    const targetIndex = this._els.tabs.indexOf(targetTab);
    if (targetIndex === -1) return;

    // Guard: evita loop quando moved di Splide richiama _activateTab su una tab già attiva
    if (targetTab.classList.contains(CLASSES.IS_ACTIVE)) return;

    this._els.tabs.forEach((tab, i) => {
      const isTarget = i === targetIndex;
      tab.classList.toggle(CLASSES.IS_ACTIVE, isTarget);
      tab.setAttribute('aria-selected', String(isTarget));
      tab.setAttribute('tabindex', isTarget ? '0' : '-1');
    });

    this._els.panels.forEach((panel) => {
      const isTarget = panel.id === targetTab.getAttribute('aria-controls');
      panel.toggleAttribute('hidden', !isTarget);
    });

    // Sync Splide: porta il carousel sulla slide corrispondente (solo se l'indice è diverso)
    if (this._splide && this._splide.index !== targetIndex) {
      this._splide.go(targetIndex);
    }

    // Desktop: scorri il track per mostrare la tab attiva (gestisce l'overflow con più di 6 voci)
    if (!this._splide) {
      requestAnimationFrame(() => {
        targetTab.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
      });
    }

    this._updateArrows();
  }

  _onTabKeydown(e) {
    const focusedIndex = this._els.tabs.indexOf(document.activeElement);
    if (focusedIndex === -1) return;

    const keyMap = {
      ArrowRight: focusedIndex + 1,
      ArrowLeft: focusedIndex - 1,
      Home: 0,
      End: this._els.tabs.length - 1,
    };

    if (!(e.key in keyMap)) return;

    e.preventDefault();
    const nextIndex = Math.max(0, Math.min(keyMap[e.key], this._els.tabs.length - 1));
    this._activateTab(this._els.tabs[nextIndex]);
    this._els.tabs[nextIndex].focus();
  }

  // Arrow navigation (desktop)
  _bindArrowEvents() {
    if (this._els.arrowPrev) {
      this._els.arrowPrev.addEventListener('click', () => this._navigateTab(-1));
    }
    if (this._els.arrowNext) {
      this._els.arrowNext.addEventListener('click', () => this._navigateTab(1));
    }
  }

  _navigateTab(direction) {
    const currentIndex = this._els.tabs.findIndex((tab) => tab.classList.contains(CLASSES.IS_ACTIVE));
    const nextIndex = currentIndex + direction;

    if (nextIndex < 0 || nextIndex >= this._els.tabs.length) return;

    this._activateTab(this._els.tabs[nextIndex]);
  }

  _updateArrows() {
    const activeIndex = this._els.tabs.findIndex((tab) => tab.classList.contains(CLASSES.IS_ACTIVE));

    if (this._els.arrowPrev) {
      this._els.arrowPrev.disabled = activeIndex === 0;
    }
    if (this._els.arrowNext) {
      this._els.arrowNext.disabled = activeIndex === this._els.tabs.length - 1;
    }
  }

  // Sostituisce lo spazio prima di € con un non-breaking space per evitare che il simbolo vada a capo da solo
  _fixEuroLineBreak() {
    document
      .querySelectorAll('.altri-avvisi-cards .altri-avvisi-card .altri-avvisi-card__description .title')
      .forEach((el) => {
        const title = el;
        title.innerHTML = title.innerHTML.replace(/\s€/g, '\u00A0€');
      });
  }

  // Accordion
  _bindAccordionEvents() {
    this._els.accordionToggles.forEach((toggle) => {
      toggle.addEventListener('click', () => this._toggleAccordion(toggle));
    });
  }

  _toggleAccordion(toggle) {
    const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
    const body = document.getElementById(toggle.getAttribute('aria-controls'));

    toggle.setAttribute('aria-expanded', String(!isExpanded));
    if (body) body.toggleAttribute('hidden', isExpanded);
  }
}
